/**
 * usePermissions hook - Check if current user has specific permissions
 * @param {object} currentUser - The current user object with permissions array
 * @returns {object} - Object with hasPermission function and helper methods
 */
export const usePermissions = (currentUser) => {
  /**
   * Check if user has a specific permission
   * @param {string} permission - The permission to check (e.g., 'notes.view')
   * @returns {boolean} - True if user has the permission
   */
  const hasPermission = (permission) => {
    if (!currentUser) return false;
    
    // Check if user has the permission in their permissions array
    if (currentUser.permissions && currentUser.permissions.includes(permission)) {
      return true;
    }
    
    // Legacy: Check if user is in Admins group (has all permissions)
    if (currentUser.groups && currentUser.groups.includes('Admins')) {
      return true;
    }
    
    return false;
  };

  /**
   * Check if user has ANY of the provided permissions
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean} - True if user has at least one permission
   */
  const hasAnyPermission = (permissions) => {
    return permissions.some(perm => hasPermission(perm));
  };

  /**
   * Check if user has ALL of the provided permissions
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean} - True if user has all permissions
   */
  const hasAllPermissions = (permissions) => {
    return permissions.every(perm => hasPermission(perm));
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
};

/**
 * Permission constants for easy reference
 * 
 * Structure:
 * - patients.* = Patient record management (view, add, edit, delete patients)
 * - patients.medications.* = Patient-specific medication actions (assign, view, edit on patient)
 * - patients.services.* = Patient-specific service actions (schedule, view, edit on patient)
 * - patients.protocols.* = Patient-specific protocol actions (create, view, edit on patient)
 * - patients.notes.* = Patient-specific note actions
 * - patients.documents.* = Patient-specific document actions
 * - medications.* = Medication catalog management (global)
 * - services.* = Service catalog management (global)
 */
export const PERMISSIONS = {
  // Patient Record Management
  PATIENTS_VIEW: 'patients.view',
  PATIENTS_ADD: 'patients.add',
  PATIENTS_EDIT: 'patients.edit',
  PATIENTS_DELETE: 'patients.delete',
  
  // Patient Medications (patient-specific)
  PATIENTS_MEDICATIONS_VIEW: 'patients.medications.view',
  PATIENTS_MEDICATIONS_ADD: 'patients.medications.add',
  PATIENTS_MEDICATIONS_EDIT: 'patients.medications.edit',
  PATIENTS_MEDICATIONS_DELETE: 'patients.medications.delete',
  
  // Patient Services (patient-specific)
  PATIENTS_SERVICES_VIEW: 'patients.services.view',
  PATIENTS_SERVICES_ADD: 'patients.services.add',
  PATIENTS_SERVICES_EDIT: 'patients.services.edit',
  PATIENTS_SERVICES_DELETE: 'patients.services.delete',
  
  // Patient Protocols (patient-specific)
  PATIENTS_PROTOCOLS_VIEW: 'patients.protocols.view',
  PATIENTS_PROTOCOLS_ADD: 'patients.protocols.add',
  PATIENTS_PROTOCOLS_EDIT: 'patients.protocols.edit',
  PATIENTS_PROTOCOLS_DELETE: 'patients.protocols.delete',
  
  // Patient Notes (patient-specific)
  PATIENTS_NOTES_VIEW: 'patients.notes.view',
  PATIENTS_NOTES_ADD: 'patients.notes.add',
  PATIENTS_NOTES_EDIT: 'patients.notes.edit',
  PATIENTS_NOTES_DELETE: 'patients.notes.delete',
  
  // Patient Documents (patient-specific)
  PATIENTS_DOCUMENTS_VIEW: 'patients.documents.view',
  PATIENTS_DOCUMENTS_ADD: 'patients.documents.add',
  PATIENTS_DOCUMENTS_DELETE: 'patients.documents.delete',
  
  // Medication Catalog Management (global)
  MEDICATIONS_VIEW: 'medications.view',
  MEDICATIONS_ADD: 'medications.add',
  MEDICATIONS_EDIT: 'medications.edit',
  MEDICATIONS_DELETE: 'medications.delete',
  
  // Service Catalog Management (global)
  SERVICES_VIEW: 'services.view',
  SERVICES_ADD: 'services.add',
  SERVICES_EDIT: 'services.edit',
  SERVICES_DELETE: 'services.delete',
  
  // User & Role Management
  USERS_VIEW: 'users.view',
  USERS_ADD: 'users.add',
  USERS_EDIT: 'users.edit',
  USERS_DELETE: 'users.delete',
  
  ROLES_VIEW: 'roles.view',
  ROLES_ADD: 'roles.add',
  ROLES_EDIT: 'roles.edit',
  ROLES_DELETE: 'roles.delete',
};